import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { User as ClerkUser } from '@clerk/backend';
import type { Content } from '@tiptap/react';
import { useConvexAuth, useQuery, useMutation, useAction } from 'convex/react';
import debounce from 'lodash.debounce';
import { AlertTriangle, Bell, Check, CheckCircle2, ChevronsUpDown, Pencil, Plus, Trash2, UserPlus, XCircle, Loader2, Database, User, Shield, Server, Table, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import AGDataGrid from '@/components/ag-data-grid';
import { ConfirmationDialog } from '@/components/confirmation-dialog';
import Container from '@/components/container';
import { TiptapEditor } from '@/components/tiptap';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAllClerkUsers } from '@/hooks/use-clerk-user';
import { useMonitorAlertEmails, useSetMonitorAlertEmails } from '@/hooks/use-monitor-alert-settings';
import {
  useUserNotificationPreferences,
  useToggleUserNotificationCategory,
  USER_NOTIFICATION_CATEGORY_INFO,
  useSystemNotificationPreferences,
  useToggleSystemNotificationCategory,
  SYSTEM_NOTIFICATION_CATEGORY_INFO,
  SYSTEM_NOTIFICATION_CATEGORY_ORDER,
  useNewBusinessUserConfig,
  useSetNewBusinessUser,
  useNewBusinessAdditionalRecipients,
  useSetNewBusinessAdditionalRecipients,
} from '@/hooks/use-notification-settings';
import type { UserNotificationCategory, SystemNotificationCategory } from '@/hooks/use-notification-settings';
import { useNylasGrants } from '@/hooks/use-nylas-grants';
import { useNylasWebhooks, useDeleteNylasWebhook } from '@/hooks/use-nylas-webhooks';
import type { Webhook } from '@/hooks/use-nylas-webhooks';
import { useGetUserSetting, useSetUserSetting } from '@/hooks/use-settings';
import { useUserTemplates } from '@/hooks/use-user-templates';
import {
  useVCEditingEnabled,
  useSetVCEditingEnabled,
  useVCWhitelistSettings,
  useUpdateVCWhitelist,
} from '@/hooks/use-verification-contact-settings';
import { convertToEmailHtml } from '@/lib/email';
import { api } from '../../../../convex/_generated/api';
import { nylasColumnDefs, webhookColumnDefs } from './components/column-defs';
import CreateConnectionSheet from './components/create-connection-sheet';
import WebhookSheetEditor from './components/webhook-sheet-editor';

function SettingsPage() {
  const [searchParams] = useSearchParams();

  // Convex auth state
  const { isLoading: isConvexLoading, isAuthenticated: isConvexAuthenticated } = useConvexAuth();
  const whoami = useQuery(api.test.whoami);
  const ensureUserMutation = useMutation(api.test.ensureUser);
  const [isEnsuring, setIsEnsuring] = useState(false);
  const [ensureResult, setEnsureResult] = useState<any>(null);

  const handleEnsureUser = async () => {
    setIsEnsuring(true);
    try {
      const result = await ensureUserMutation();
      setEnsureResult(result);
      toast.success('User synced to Convex successfully!');
    } catch (error) {
      toast.error('Failed to sync user', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsEnsuring(false);
    }
  };

  // Hasura test state
  const testHasuraAction = useAction(api.test.testHasura);
  const [isTestingHasura, setIsTestingHasura] = useState(false);
  const [hasuraResult, setHasuraResult] = useState<any>(null);

  const handleTestHasura = async () => {
    setIsTestingHasura(true);
    setHasuraResult(null);
    try {
      const result = await testHasuraAction();
      setHasuraResult(result);
      if (result.success) {
        toast.success('Hasura connection successful!');
      } else {
        toast.error('Hasura connection failed', {
          description: result.error,
        });
      }
    } catch (error) {
      toast.error('Failed to test Hasura connection', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
      setHasuraResult({ success: false, error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      setIsTestingHasura(false);
    }
  };

  // Check URL parameters for success message
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') {
      toast.success('Successfully added email connection!');
    } else if (success === 'false') {
      toast.error('Adding email connection was unsuccessful.');
    }
  }, [searchParams]);

  // Communications default to verify setting
  const { data: commDefaultSetting, isLoading: isCommDefaultLoading, error: commDefaultError } = useGetUserSetting('communications_default_to_verify');
  const setCommDefault = useSetUserSetting();

  const commDefaultValue = commDefaultSetting?.value === 'true';

  // Ref to track if initial setting check/creation was done
  const initialCommDefaultCheckDone = useRef(false);

  // Effect to create the setting with default 'false' if it doesn't exist on load
  useEffect(() => {
    if (
      !isCommDefaultLoading
      && commDefaultSetting === undefined
      && !initialCommDefaultCheckDone.current
    ) {
      console.warn(
        'User setting "communications_default_to_verify" not found in Settings. Applying default \'false\' and creating setting.',
      );
      setCommDefault.mutate({ key: 'communications_default_to_verify', value: 'false' });
      initialCommDefaultCheckDone.current = true; // Mark as done
    }
  }, [isCommDefaultLoading, commDefaultSetting, setCommDefault]);

  const handleCommDefaultChange = (checked: boolean) => {
    setCommDefault.mutate({ key: 'communications_default_to_verify', value: checked ? 'true' : 'false' });
  };

  // Email signature state
  const {
    template: personalFooter,
    updateTemplate,
    createTemplate,
    isLoading: isUserTemplateLoading,
    error: templateError,
  } = useUserTemplates({
    name: 'personal-footer',
  });
  const editorContent = personalFooter?.template || '<p></p>';

  // Show error toast if template fetch fails
  useEffect(() => {
    if (templateError) {
      toast.error('Failed to load signature template', {
        description: templateError.message,
      });
    }
  }, [templateError]);

  // Stable function to update the signature logic
  const updateSignatureCallback = useCallback(async (content: string) => {
    if (!isUserTemplateLoading) {
      try {
        const processedHtml = convertToEmailHtml(content);
        await (personalFooter
          ? updateTemplate({ id: personalFooter.id, template: processedHtml })
          : createTemplate({ name: 'personal-footer', template: processedHtml })
        );
        // Toast will be shown after successful debounced execution
      } catch (error) {
        toast.error('Failed to update signature', {
          description: error instanceof Error ? error.message : 'An unknown error occurred',
        });
        // Re-throw if needed or handle differently
      }
    }
  }, [updateTemplate, createTemplate, personalFooter, isUserTemplateLoading]);

  // Create a memoized debounced update function
  const debouncedUpdate = useMemo(
    () => debounce(async (content: string) => {
      try {
        await updateSignatureCallback(content);
        toast.success('Signature updated successfully');
      } catch {
        // Error is already handled in updateSignatureCallback with a toast
      }
    }, 1000),
    [updateSignatureCallback], // Dependency array for useMemo
  );

  // Handle signature changes by calling the debounced update function
  const handleSignatureChange = useCallback(
    (content: Content) => {
      if (typeof content === 'string') {
        void debouncedUpdate(content);
      }
    },
    [debouncedUpdate], // Dependency is the memoized debounced function
  );

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]); // Dependency is the memoized debounced function

  // User notification preferences (per-user settings)
  const {
    data: userNotificationPreferences,
    isLoading: isUserNotificationPrefsLoading,
    error: userNotificationPrefsError,
  } = useUserNotificationPreferences();
  const toggleUserNotification = useToggleUserNotificationCategory();

  const handleUserNotificationToggle = (category: UserNotificationCategory, enabled: boolean) => {
    toggleUserNotification.mutate(
      { category, enabled },
      {
        onError: (error) => {
          toast.error('Failed to update notification preference', {
            description: error instanceof Error ? error.message : 'Unknown error',
          });
        },
      },
    );
  };

  // System notification preferences (global settings)
  const {
    data: systemNotificationPreferences,
    isLoading: isSystemNotificationPrefsLoading,
    error: systemNotificationPrefsError,
  } = useSystemNotificationPreferences();
  const toggleSystemNotification = useToggleSystemNotificationCategory();

  const handleSystemNotificationToggle = (category: SystemNotificationCategory, enabled: boolean) => {
    toggleSystemNotification.mutate(
      { category, enabled },
      {
        onError: (error) => {
          toast.error('Failed to update system notification setting', {
            description: error instanceof Error ? error.message : 'Unknown error',
          });
        },
      },
    );
  };

  // New business user config
  const { data: newBusinessUserConfig, isLoading: isNewBizConfigLoading } = useNewBusinessUserConfig();
  const setNewBusinessUser = useSetNewBusinessUser();
  const { data: allClerkUsers } = useAllClerkUsers();
  const [newBizUserPopoverOpen, setNewBizUserPopoverOpen] = useState(false);

  const selectedNewBizUser = useMemo(() => {
    if (!newBusinessUserConfig?.userId || !allClerkUsers)
      return null;
    return allClerkUsers.find((u) => u?.id === newBusinessUserConfig.userId) ?? null;
  }, [newBusinessUserConfig?.userId, allClerkUsers]);

  const handleSelectNewBizUser = (userId: string) => {
    setNewBizUserPopoverOpen(false);
    if (userId === newBusinessUserConfig?.userId)
      return;
    setNewBusinessUser.mutate(userId, {
      onSuccess: () => toast.success('New business user updated'),
      onError: (error) => {
        toast.error('Failed to update new business user', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      },
    });
  };

  // Additional new business email recipients
  const { data: additionalRecipientsData } = useNewBusinessAdditionalRecipients();
  const setAdditionalRecipients = useSetNewBusinessAdditionalRecipients();
  const additionalRecipientIds = additionalRecipientsData?.userIds ?? [];
  const [addRecipientPopoverOpen, setAddRecipientPopoverOpen] = useState(false);

  const additionalRecipientUsers = useMemo(() => {
    if (additionalRecipientIds.length === 0 || !allClerkUsers)
      return [];
    return additionalRecipientIds
      .map((id) => allClerkUsers.find((u) => u?.id === id))
      .filter((u): u is Partial<import('@clerk/backend').User> => !!u);
  }, [additionalRecipientIds, allClerkUsers]);

  const handleAddRecipient = (userId: string) => {
    if (additionalRecipientIds.includes(userId))
      return;
    setAdditionalRecipients.mutate([...additionalRecipientIds, userId], {
      onError: (error) => {
        toast.error('Failed to add recipient', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      },
    });
  };

  const handleRemoveRecipient = (userId: string) => {
    setAdditionalRecipients.mutate(additionalRecipientIds.filter((id) => id !== userId), {
      onError: (error) => {
        toast.error('Failed to remove recipient', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      },
    });
  };

  // Verification contact editing feature flag
  const { data: vcEditingData, isLoading: isVCEditingLoading } = useVCEditingEnabled();
  const setVCEditing = useSetVCEditingEnabled();
  const vcEditingEnabled = vcEditingData?.enabled ?? false;

  const { data: vcWhitelistData, isLoading: isVCWhitelistLoading } = useVCWhitelistSettings();
  const updateVCWhitelist = useUpdateVCWhitelist();
  const whitelistEnabled = vcWhitelistData?.enabled ?? true;
  const whitelistEmails = vcWhitelistData?.emails ?? [];

  const [newWhitelistEmail, setNewWhitelistEmail] = useState('');

  // Monitor worker down-alert recipients (Redis-backed via API)
  const { data: alertEmails, isLoading: isAlertEmailsLoading } = useMonitorAlertEmails();
  const setAlertEmails = useSetMonitorAlertEmails();
  const [newAlertEmail, setNewAlertEmail] = useState('');

  const handleAddAlertEmail = () => {
    const email = newAlertEmail.trim().toLowerCase();
    if (!email || (alertEmails ?? []).includes(email))
      return;
    setAlertEmails.mutate([...(alertEmails ?? []), email], {
      onSuccess: () => setNewAlertEmail(''),
      onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to add email'),
    });
  };

  const handleRemoveAlertEmail = (email: string) => {
    setAlertEmails.mutate((alertEmails ?? []).filter((e) => e !== email), {
      onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to remove email'),
    });
  };

  const handleVCEditingToggle = (checked: boolean) => {
    setVCEditing.mutate(checked, {
      onError: (error) => {
        toast.error('Failed to update feature flag', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      },
    });
  };

  const handleWhitelistEnabledToggle = (checked: boolean) => {
    updateVCWhitelist.mutate({ enabled: checked }, {
      onError: (error) => {
        toast.error('Failed to update whitelist setting', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      },
    });
  };

  const handleAddWhitelistEmail = () => {
    const email = newWhitelistEmail.trim().toLowerCase();
    if (!email || whitelistEmails.includes(email))
      return;
    updateVCWhitelist.mutate({ emails: [...whitelistEmails, email] }, {
      onSuccess: () => setNewWhitelistEmail(''),
      onError: (error) => {
        toast.error('Failed to add email', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      },
    });
  };

  const handleRemoveWhitelistEmail = (emailToRemove: string) => {
    updateVCWhitelist.mutate({ emails: whitelistEmails.filter((e) => e !== emailToRemove) }, {
      onError: (error) => {
        toast.error('Failed to remove email', {
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      },
    });
  };

  // State for Connections tab
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState<any | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { data: grants, isLoading: isGrantLoading, deleteGrant, isDeletingGrant } = useNylasGrants();

  // State for Webhooks tab
  const [webhookSearchQuery, setWebhookSearchQuery] = useState('');
  const [isCreateWebhookSheetOpen, setIsCreateWebhookSheetOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [isDeleteWebhookDialogOpen, setIsDeleteWebhookDialogOpen] = useState(false);
  const [isEditWebhookSheetOpen, setIsEditWebhookSheetOpen] = useState(false);
  const { data: webhooks, isLoading: isLoadingWebhooks } = useNylasWebhooks();
  const { mutate: deleteWebhook, isPending: isDeletingWebhook } = useDeleteNylasWebhook();

  // Connections handlers
  const handleConnectionSelectionChanged = (event: any) => {
    const selectedRows = event.api.getSelectedRows();
    setSelectedGrant(selectedRows.length === 1 ? selectedRows[0] : null);
  };

  const handleDeleteConnection = async () => {
    if (selectedGrant) {
      deleteGrant(selectedGrant.id);
      setSelectedGrant(null);
      setIsDeleteDialogOpen(false);
    }
  };

  // Webhooks handlers
  const handleWebhookSelectionChanged = (event: any) => {
    const selectedRows = event.api.getSelectedRows();
    setSelectedWebhook(selectedRows.length === 1 ? selectedRows[0] : null);
  };

  const handleDeleteWebhook = () => {
    if (selectedWebhook) {
      deleteWebhook(selectedWebhook.id, {
        onSuccess: () => {
          setSelectedWebhook(null);
          setIsDeleteWebhookDialogOpen(false);
        },
      });
    }
  };

  return (
    <Container className="mt-8 pb-12">
      <div className="mb-8 flex justify-between">
        <div>
          <h1 className="text-xl font-bold">Settings</h1>
        </div>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Communications Default Account</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <Switch
            checked={commDefaultValue}
            onCheckedChange={handleCommDefaultChange}
            disabled={isCommDefaultLoading || setCommDefault.isPending}
            id="communications-default-to-verify"
          />
          <div>
            <div className="font-medium">Default to verify@ftld.com for communications</div>
            <CardDescription>
              If enabled, the left pane selector in communications will default to verify@ftld.com. If disabled, it will default to your email.
            </CardDescription>
            {commDefaultError && (
              <div className="mt-1 text-sm text-destructive">
                {commDefaultError.message}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Email Signature</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col space-y-2">
          {isUserTemplateLoading
            ? (
                <div className="flex h-64 items-center justify-center rounded-md border bg-muted/50">
                  <p className="text-sm text-muted-foreground">Loading signature...</p>
                </div>
              )
            : (
                <TiptapEditor
                  className="h-64"
                  value={editorContent}
                  onChange={handleSignatureChange}
                  placeholder="Enter your email signature..."
                  hideSections={['one', 'tasks']}
                  editorContentClassName="prose prose-sm max-w-none focus:outline-none overflow-y-auto px-3 py-2"
                />
              )}
          <CardDescription>
            Your email signature will be available in templates using the
            {' '}
            <span className="font-mono">{'{{PERSONALFOOTER}}'}</span>
            {' '}
            variable.
          </CardDescription>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="size-5" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Control which task assignment emails you receive. These settings only affect automated notifications, not manually composed emails.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isUserNotificationPrefsLoading
            ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              )
            : (userNotificationPrefsError
                ? (
                    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                      Failed to load notification preferences. Please refresh the page.
                    </div>
                  )
                : (
                    <div className="space-y-4">
                      {(Object.keys(USER_NOTIFICATION_CATEGORY_INFO) as UserNotificationCategory[]).map((category) => {
                        const info = USER_NOTIFICATION_CATEGORY_INFO[category];
                        const isEnabled = userNotificationPreferences?.[category] ?? true;
                        const isToggling = toggleUserNotification.isPending
                          && toggleUserNotification.variables?.category === category;

                        return (
                          <div
                            key={category}
                            className="flex items-start gap-4 rounded-lg border p-4 transition-colors hover:bg-muted/50"
                          >
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={(checked) => handleUserNotificationToggle(category, checked)}
                              disabled={isToggling}
                              id={`notification-${category}`}
                              className="mt-0.5"
                            />
                            <div className="flex-1">
                              <label
                                htmlFor={`notification-${category}`}
                                className="cursor-pointer font-medium"
                              >
                                {info.label}
                              </label>
                              <p className="mt-1 text-sm text-muted-foreground">
                                {info.description}
                              </p>
                            </div>
                            {isToggling && (
                              <Loader2 className="size-4 animate-spin text-muted-foreground" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="size-5" />
            System Email Settings
          </CardTitle>
          <CardDescription>
            Global settings that control automated system emails. These emails are sent to designated role accounts (configured via environment variables).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 size-5 shrink-0" />
            <div>
              <p className="font-semibold">Caution</p>
              <p className="mt-1 text-sm">
                These settings affect critical system notifications. Disabling them may cause important events to go unnoticed. Only modify if you understand the implications.
              </p>
            </div>
          </div>

          {isSystemNotificationPrefsLoading
            ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              )
            : (systemNotificationPrefsError
                ? (
                    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                      Failed to load system notification settings. Please refresh the page.
                    </div>
                  )
                : (
                    <div className="space-y-4">
                      {SYSTEM_NOTIFICATION_CATEGORY_ORDER.map((category) => {
                        const info = SYSTEM_NOTIFICATION_CATEGORY_INFO[category];
                        const isEnabled = systemNotificationPreferences?.[category] ?? true;
                        const isToggling = toggleSystemNotification.isPending
                          && toggleSystemNotification.variables?.category === category;

                        return (
                          <div
                            key={category}
                            className="space-y-0 rounded-lg border transition-colors hover:bg-muted/50"
                          >
                            <div className="flex items-start gap-4 p-4">
                              <Switch
                                checked={isEnabled}
                                onCheckedChange={(checked) => handleSystemNotificationToggle(category, checked)}
                                disabled={isToggling}
                                id={`system-notification-${category}`}
                                className="mt-0.5"
                              />
                              <div className="flex-1">
                                <label
                                  htmlFor={`system-notification-${category}`}
                                  className="cursor-pointer font-medium"
                                >
                                  {info.label}
                                </label>
                                <p className="mt-1 text-sm text-muted-foreground">
                                  {info.description}
                                </p>
                                {category !== 'new_business_tasks' && (
                                  <p className="mt-1 text-xs text-muted-foreground/70">
                                    Recipient:
                                    {' '}
                                    <code className="rounded bg-muted px-1">{info.recipient}</code>
                                  </p>
                                )}
                              </div>
                              {isToggling && (
                                <Loader2 className="size-4 animate-spin text-muted-foreground" />
                              )}
                            </div>

                            {category === 'new_business_tasks' && (
                              <div className="border-t px-4 py-3">
                                <p className="mb-2 text-xs font-medium text-muted-foreground">Assigned To</p>
                                {isNewBizConfigLoading
                                  ? (
                                      <div className="flex items-center gap-2 py-1">
                                        <Loader2 className="size-3 animate-spin text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">Loading...</span>
                                      </div>
                                    )
                                  : (
                                      <Popover open={newBizUserPopoverOpen} onOpenChange={setNewBizUserPopoverOpen}>
                                        <PopoverTrigger asChild>
                                          <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={newBizUserPopoverOpen}
                                            className="h-9 w-full justify-between font-normal"
                                            disabled={setNewBusinessUser.isPending}
                                          >
                                            {setNewBusinessUser.isPending
                                              ? (
                                                  <span className="flex items-center gap-2 text-muted-foreground">
                                                    <Loader2 className="size-3 animate-spin" />
                                                    Saving...
                                                  </span>
                                                )
                                              : (selectedNewBizUser
                                                  ? (
                                                      <span className="flex items-center gap-2 truncate">
                                                        <Avatar className="size-5 shrink-0">
                                                          <AvatarImage src={selectedNewBizUser.imageUrl} />
                                                          <AvatarFallback className="text-[10px]">
                                                            {selectedNewBizUser.firstName?.[0]}
                                                            {selectedNewBizUser.lastName?.[0]}
                                                          </AvatarFallback>
                                                        </Avatar>
                                                        <span className="truncate text-sm">
                                                          {selectedNewBizUser.firstName}
                                                          {' '}
                                                          {selectedNewBizUser.lastName}
                                                        </span>
                                                        <span className="truncate text-xs text-muted-foreground">
                                                          {(selectedNewBizUser as ClerkUser).primaryEmailAddress?.emailAddress
                                                            ?? (selectedNewBizUser as any).emailAddresses?.[0]?.emailAddress}
                                                        </span>
                                                      </span>
                                                    )
                                                  : (
                                                      <span className="text-muted-foreground">Select user...</span>
                                                    ))}
                                            <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                          <Command>
                                            <CommandInput placeholder="Search users..." />
                                            <CommandEmpty>No users found.</CommandEmpty>
                                            <CommandGroup className="max-h-[250px] overflow-y-auto">
                                              {allClerkUsers?.map((user) => {
                                                if (!user?.id)
                                                  return null;
                                                const isSelected = user.id === newBusinessUserConfig?.userId;
                                                const email = (user as ClerkUser).primaryEmailAddress?.emailAddress
                                                  ?? (user as any).emailAddresses?.[0]?.emailAddress;
                                                return (
                                                  <CommandItem
                                                    key={user.id}
                                                    value={`${user.firstName} ${user.lastName} ${email}`}
                                                    onSelect={() => handleSelectNewBizUser(user.id!)}
                                                    className="gap-3"
                                                  >
                                                    <Avatar className="size-7 shrink-0">
                                                      <AvatarImage src={user.imageUrl} />
                                                      <AvatarFallback className="text-xs">
                                                        {user.firstName?.[0]}
                                                        {user.lastName?.[0]}
                                                      </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex min-w-0 flex-1 flex-col">
                                                      <span className="truncate text-sm font-medium">
                                                        {user.firstName}
                                                        {' '}
                                                        {user.lastName}
                                                      </span>
                                                      <span className="truncate text-xs text-muted-foreground">
                                                        {email}
                                                      </span>
                                                    </div>
                                                    {isSelected && <Check className="size-4 shrink-0 text-primary" />}
                                                  </CommandItem>
                                                );
                                              })}
                                            </CommandGroup>
                                          </Command>
                                        </PopoverContent>
                                      </Popover>
                                    )}
                                {newBusinessUserConfig?.source === 'environment' && (
                                  <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                                    Using environment variable fallback. Select a user above to override.
                                  </p>
                                )}

                                <div className="mt-4">
                                  <p className="mb-2 text-xs font-medium text-muted-foreground">Also Notify</p>
                                  <p className="mb-2 text-xs text-muted-foreground">
                                    These users will receive the task email but won't be assigned to the task.
                                  </p>

                                  {additionalRecipientUsers.length > 0 && (
                                    <div className="mb-2 space-y-1">
                                      {additionalRecipientUsers.map((user) => {
                                        const email = (user as ClerkUser).primaryEmailAddress?.emailAddress
                                          ?? (user as any).emailAddresses?.[0]?.emailAddress;
                                        return (
                                          <div
                                            key={user.id}
                                            className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-1.5"
                                          >
                                            <Avatar className="size-5 shrink-0">
                                              <AvatarImage src={user.imageUrl} />
                                              <AvatarFallback className="text-[10px]">
                                                {user.firstName?.[0]}
                                                {user.lastName?.[0]}
                                              </AvatarFallback>
                                            </Avatar>
                                            <span className="truncate text-sm">
                                              {user.firstName}
                                              {' '}
                                              {user.lastName}
                                            </span>
                                            <span className="truncate text-xs text-muted-foreground">
                                              {email}
                                            </span>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="ml-auto size-6 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                                              onClick={() => handleRemoveRecipient(user.id!)}
                                              disabled={setAdditionalRecipients.isPending}
                                            >
                                              <X className="size-3" />
                                            </Button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  <Popover open={addRecipientPopoverOpen} onOpenChange={setAddRecipientPopoverOpen}>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 gap-2"
                                        disabled={setAdditionalRecipients.isPending}
                                      >
                                        {setAdditionalRecipients.isPending
                                          ? <Loader2 className="size-3 animate-spin" />
                                          : <UserPlus className="size-3.5" />}
                                        Add recipient
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[350px] p-0" align="start">
                                      <Command>
                                        <CommandInput placeholder="Search users..." />
                                        <CommandEmpty>No users found.</CommandEmpty>
                                        <CommandGroup className="max-h-[250px] overflow-y-auto">
                                          {allClerkUsers?.map((user) => {
                                            if (!user?.id)
                                              return null;
                                            if (user.id === newBusinessUserConfig?.userId)
                                              return null;
                                            const isAdded = additionalRecipientIds.includes(user.id);
                                            const email = (user as ClerkUser).primaryEmailAddress?.emailAddress
                                              ?? (user as any).emailAddresses?.[0]?.emailAddress;
                                            return (
                                              <CommandItem
                                                key={user.id}
                                                value={`${user.firstName} ${user.lastName} ${email}`}
                                                onSelect={() => {
                                                  if (isAdded) {
                                                    handleRemoveRecipient(user.id!);
                                                  } else {
                                                    handleAddRecipient(user.id!);
                                                  }
                                                }}
                                                className="gap-3"
                                              >
                                                <Avatar className="size-7 shrink-0">
                                                  <AvatarImage src={user.imageUrl} />
                                                  <AvatarFallback className="text-xs">
                                                    {user.firstName?.[0]}
                                                    {user.lastName?.[0]}
                                                  </AvatarFallback>
                                                </Avatar>
                                                <div className="flex min-w-0 flex-1 flex-col">
                                                  <span className="truncate text-sm font-medium">
                                                    {user.firstName}
                                                    {' '}
                                                    {user.lastName}
                                                  </span>
                                                  <span className="truncate text-xs text-muted-foreground">
                                                    {email}
                                                  </span>
                                                </div>
                                                {isAdded && <Check className="size-4 shrink-0 text-primary" />}
                                              </CommandItem>
                                            );
                                          })}
                                        </CommandGroup>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="size-5" />
            Monitor Worker Down Alerts
          </CardTitle>
          <CardDescription>
            Email addresses notified when the local scan worker box goes down. The old monitor will kick in if this is triggered so the monitor never goes down completely.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="ops@ftld.com"
              value={newAlertEmail}
              onChange={(e) => setNewAlertEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter')
                  return;
                e.preventDefault();
                handleAddAlertEmail();
              }}
              disabled={setAlertEmails.isPending}
            />
            <Button size="sm" onClick={handleAddAlertEmail} disabled={!newAlertEmail.trim() || setAlertEmails.isPending}>
              <Plus className="mr-1 size-4" />
              Add
            </Button>
          </div>
          {isAlertEmailsLoading
            ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              )
            : ((alertEmails ?? []).length === 0
                ? (
                    <p className="text-sm text-muted-foreground">
                      No recipients configured. Add at least one to receive worker-down alerts.
                    </p>
                  )
                : (
                    <div className="space-y-1">
                      {(alertEmails ?? []).map((email) => (
                        <div key={email} className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-sm">
                          <span className="font-mono">{email}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="size-6 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveAlertEmail(email)}
                            disabled={setAlertEmails.isPending}
                          >
                            <X className="size-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))}
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="size-5" />
            Verification Contact Editing
            <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Beta</span>
          </CardTitle>
          <CardDescription>
            Phased rollout control for allowing verification contact details to be reviewed and updated via the public verification form. Disabled by default — will never activate due to Redis failures or missing configuration.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 size-5 shrink-0" />
            <p className="text-sm">
              When enabled, eligible deals will show a verification contact section on the public form. Use the whitelist below to limit which registrant emails see this feature during rollout.
            </p>
          </div>

          {isVCEditingLoading
            ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              )
            : (
                <>
                  <div className="flex items-start gap-4 rounded-lg border p-4 transition-colors hover:bg-muted/50">
                    <Switch
                      checked={vcEditingEnabled}
                      onCheckedChange={handleVCEditingToggle}
                      disabled={setVCEditing.isPending}
                      id="vc-editing-enabled"
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <label htmlFor="vc-editing-enabled" className="cursor-pointer font-medium">
                        Enable Verification Contact Editing
                      </label>
                      <p className="mt-1 text-sm text-muted-foreground">
                        When on, the public verification form will include a section for reviewing and updating verification contact details (name, title, email, phone).
                      </p>
                    </div>
                    {setVCEditing.isPending && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                  </div>

                  {vcEditingEnabled && (
                    <div className="ml-4 space-y-4 border-l-2 border-muted pl-4">
                      {isVCWhitelistLoading
                        ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="size-5 animate-spin text-muted-foreground" />
                            </div>
                          )
                        : (
                            <>
                              <div className="flex items-start gap-4 rounded-lg border p-4 transition-colors hover:bg-muted/50">
                                <Switch
                                  checked={whitelistEnabled}
                                  onCheckedChange={handleWhitelistEnabledToggle}
                                  disabled={updateVCWhitelist.isPending}
                                  id="vc-whitelist-enabled"
                                  className="mt-0.5"
                                />
                                <div className="flex-1">
                                  <label htmlFor="vc-whitelist-enabled" className="cursor-pointer font-medium">
                                    Restrict to Whitelisted Registrant Emails
                                  </label>
                                  <p className="mt-1 text-sm text-muted-foreground">
                                    When on, only deals where the registrant email appears in the list below will show the VC editing section. Turn off to enable for all deals.
                                  </p>
                                </div>
                                {updateVCWhitelist.isPending && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                              </div>

                              {whitelistEnabled && (
                                <div className="space-y-3 rounded-lg border p-4">
                                  <p className="text-sm font-medium">Whitelisted Registrant Emails</p>
                                  <div className="flex gap-2">
                                    <Input
                                      type="email"
                                      placeholder="registrant@example.com"
                                      value={newWhitelistEmail}
                                      onChange={(e) => setNewWhitelistEmail(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleAddWhitelistEmail();
                                        }
                                      }}
                                      disabled={updateVCWhitelist.isPending}
                                    />
                                    <Button
                                      size="sm"
                                      onClick={handleAddWhitelistEmail}
                                      disabled={!newWhitelistEmail.trim() || updateVCWhitelist.isPending}
                                    >
                                      <Plus className="mr-1 size-4" />
                                      Add
                                    </Button>
                                  </div>
                                  {whitelistEmails.length === 0
                                    ? (
                                        <p className="text-sm text-muted-foreground">
                                          No emails in the whitelist. Add at least one, or disable the whitelist to enable for all deals.
                                        </p>
                                      )
                                    : (
                                        <div className="space-y-1">
                                          {whitelistEmails.map((email) => (
                                            <div key={email} className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-sm">
                                              <span className="font-mono">{email}</span>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="size-6 p-0 text-muted-foreground hover:text-destructive"
                                                onClick={() => handleRemoveWhitelistEmail(email)}
                                                disabled={updateVCWhitelist.isPending}
                                              >
                                                <X className="size-3" />
                                              </Button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                </div>
                              )}
                            </>
                          )}
                    </div>
                  )}
                </>
              )}
        </CardContent>
      </Card>

      <Tabs defaultValue="connections" className="space-y-4">
        <TabsList>
          <TabsTrigger value="connections">Connections</TabsTrigger>
          <TabsTrigger value="webhooks">Web Hooks</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
        </TabsList>

        <TabsContent value="connections">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle>Email Connections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Input
                  type="search"
                  placeholder="Search connections..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <AGDataGrid
                columnDefs={nylasColumnDefs}
                rowData={grants}
                quickFilterText={searchQuery}
                rowSelection={{ mode: 'singleRow' }}
                onSelectionChanged={handleConnectionSelectionChanged}
                loadingOverlayComponent={isGrantLoading ? 'Loading...' : undefined}
              />
              <div className="mt-4 flex justify-end gap-2">
                <Button
                  className="ml-auto"
                  onClick={() => setIsCreateSheetOpen(true)}
                  size="sm"
                >
                  <Plus className="mr-2 size-4" />
                  Add New Connection
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  size="sm"
                  disabled={!selectedGrant || isDeletingGrant}
                >
                  <Trash2 className="mr-2 size-4" />
                  Delete Connection
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle>Active Webhooks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Input
                  type="search"
                  placeholder="Search webhooks..."
                  value={webhookSearchQuery}
                  onChange={(e) => setWebhookSearchQuery(e.target.value)}
                />
              </div>
              <AGDataGrid
                columnDefs={webhookColumnDefs}
                rowData={webhooks}
                quickFilterText={webhookSearchQuery}
                rowSelection={{ mode: 'singleRow' }}
                onSelectionChanged={handleWebhookSelectionChanged}
                onRowDoubleClicked={() => {
                  if (selectedWebhook) {
                    setIsEditWebhookSheetOpen(true);
                  }
                }}
                loadingOverlayComponent={isLoadingWebhooks ? 'Loading...' : undefined}
              />
              <div className="mt-4 flex justify-end gap-2">
                {/* <Button
                  className="ml-auto"
                  onClick={() => setIsCreateWebhookSheetOpen(true)}
                  size="sm"
                >
                  <Plus className="mr-2 size-4" />
                  Add New Webhook
                </Button> */}
                <Button
                  onClick={() => setIsEditWebhookSheetOpen(true)}
                  size="sm"
                  disabled={!selectedWebhook}
                >
                  <Pencil className="mr-2 size-4" />
                  Edit Webhook
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteWebhookDialogOpen(true)}
                  size="sm"
                  disabled={!selectedWebhook || isDeletingWebhook}
                >
                  <Trash2 className="mr-2 size-4" />
                  Delete Webhook
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Convex Integration Test
              </CardTitle>
              <CardDescription>
                Verify Clerk + Convex authentication is working correctly
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Auth Status */}
              <div className="rounded-lg border p-4">
                <h4 className="mb-3 flex items-center gap-2 font-medium">
                  <Shield className="size-4" />
                  Authentication Status
                </h4>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Convex Connection</span>
                    {isConvexLoading
                      ? (
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Loader2 className="size-3 animate-spin" />
                            Loading...
                          </span>
                        )
                      : (isConvexAuthenticated
                          ? (
                              <span className="flex items-center gap-1 text-sm text-green-600">
                                <CheckCircle2 className="size-4" />
                                Connected
                              </span>
                            )
                          : (
                              <span className="flex items-center gap-1 text-sm text-red-600">
                                <XCircle className="size-4" />
                                Not Authenticated
                              </span>
                            ))}
                  </div>
                </div>
              </div>

              {/* Identity Info */}
              <div className="rounded-lg border p-4">
                <h4 className="mb-3 flex items-center gap-2 font-medium">
                  <User className="size-4" />
                  Identity Information
                </h4>
                {whoami === undefined
                  ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="size-3 animate-spin" />
                        Loading identity...
                      </div>
                    )
                  : (whoami?.authenticated
                      ? (
                          <div className="space-y-2 text-sm">
                            <div className="grid grid-cols-[120px_1fr] gap-2">
                              <span className="text-muted-foreground">Issuer:</span>
                              <code className="rounded bg-muted px-1 text-xs">{whoami.issuer || 'N/A'}</code>
                            </div>
                            <div className="grid grid-cols-[120px_1fr] gap-2">
                              <span className="text-muted-foreground">Subject:</span>
                              <code className="rounded bg-muted px-1 text-xs">{whoami.subject}</code>
                            </div>
                            <div className="grid grid-cols-[120px_1fr] gap-2">
                              <span className="text-muted-foreground">Email:</span>
                              <span>{whoami.email || 'N/A'}</span>
                            </div>
                            <div className="grid grid-cols-[120px_1fr] gap-2">
                              <span className="text-muted-foreground">Name:</span>
                              <span>{whoami.name || 'N/A'}</span>
                            </div>
                          </div>
                        )
                      : (
                          <p className="text-sm text-muted-foreground">{whoami?.message || 'Not logged in'}</p>
                        ))}
              </div>

              {/* Sync User Action */}
              <div className="rounded-lg border p-4">
                <h4 className="mb-3 font-medium">Sync User to Convex</h4>
                <p className="mb-4 text-sm text-muted-foreground">
                  Create or update your user profile in the Convex database. This ensures your Clerk identity is synced.
                </p>
                <Button
                  onClick={handleEnsureUser}
                  disabled={!isConvexAuthenticated || isEnsuring}
                  size="sm"
                >
                  {isEnsuring
                    ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Syncing...
                        </>
                      )
                    : (
                        <>
                          <Database className="mr-2 size-4" />
                          Sync User
                        </>
                      )}
                </Button>

                {ensureResult && (
                  <div className="mt-4 rounded-lg bg-muted p-3">
                    <p className="mb-2 text-sm font-medium text-green-600">✓ User synced successfully</p>
                    <div className="space-y-1 text-xs">
                      <div className="grid grid-cols-[80px_1fr] gap-2">
                        <span className="text-muted-foreground">User ID:</span>
                        <code className="rounded bg-background px-1">{ensureResult.user?._id}</code>
                      </div>
                      <div className="grid grid-cols-[80px_1fr] gap-2">
                        <span className="text-muted-foreground">Email:</span>
                        <span>{ensureResult.user?.email || 'N/A'}</span>
                      </div>
                      <div className="grid grid-cols-[80px_1fr] gap-2">
                        <span className="text-muted-foreground">Name:</span>
                        <span>{ensureResult.user?.name || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Hasura Connection Test */}
              <div className="rounded-lg border p-4">
                <h4 className="mb-3 flex items-center gap-2 font-medium">
                  <Server className="size-4" />
                  Hasura Connection Test
                </h4>
                <p className="mb-4 text-sm text-muted-foreground">
                  Test the connection from Convex to Hasura GraphQL. This verifies that environment variables are configured correctly and the GraphQL endpoint is reachable.
                </p>
                <Button
                  onClick={handleTestHasura}
                  disabled={!isConvexAuthenticated || isTestingHasura}
                  size="sm"
                >
                  {isTestingHasura
                    ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Testing...
                        </>
                      )
                    : (
                        <>
                          <Server className="mr-2 size-4" />
                          Test Hasura Connection
                        </>
                      )}
                </Button>

                {hasuraResult && (
                  <div className="mt-4 rounded-lg bg-muted p-3">
                    {hasuraResult.success
                      ? (
                          <>
                            <p className="mb-2 text-sm font-medium text-green-600">✓ Hasura connection successful</p>
                            <div className="space-y-2 text-xs">
                              <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-muted-foreground">Endpoint:</span>
                                <code className="truncate rounded bg-background px-1">{hasuraResult.connection?.graphqlEndpoint}</code>
                              </div>
                              <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-muted-foreground">Status:</span>
                                <span className="flex items-center gap-1">
                                  <CheckCircle2 className="size-3 text-green-600" />
                                  {hasuraResult.connection?.status}
                                </span>
                              </div>
                              <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-muted-foreground">Total Domains:</span>
                                <span>{hasuraResult.data?.totalDomains?.toLocaleString()}</span>
                              </div>
                              {hasuraResult.data?.sampleDomains?.length > 0 && (
                                <div className="mt-2">
                                  <span className="flex items-center gap-1 text-muted-foreground">
                                    <Table className="size-3" />
                                    Sample Domains (most recently updated):
                                  </span>
                                  <div className="mt-2 space-y-1.5">
                                    {hasuraResult.data?.sampleDomains?.map((domain: { name: string; registrar: string | null; lastVerified: string | null }) => (
                                      <div key={domain.name} className="flex items-center gap-2 rounded bg-background px-2 py-1">
                                        <code className="font-medium">{domain.name}</code>
                                        {domain.registrar && (
                                          <span className="text-muted-foreground">
                                            •
                                            {' '}
                                            {domain.registrar}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )
                      : (
                          <>
                            <p className="mb-2 text-sm font-medium text-red-600">✗ Hasura connection failed</p>
                            <div className="text-xs">
                              <span className="text-muted-foreground">Error: </span>
                              <code className="rounded bg-background px-1 text-red-600">{hasuraResult.error}</code>
                            </div>
                          </>
                        )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Sheets */}
      <CreateConnectionSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
      />

      <WebhookSheetEditor
        open={isCreateWebhookSheetOpen}
        onOpenChange={setIsCreateWebhookSheetOpen}
      />

      <WebhookSheetEditor
        open={isEditWebhookSheetOpen}
        onOpenChange={setIsEditWebhookSheetOpen}
        webhook={selectedWebhook}
      />

      {/* Dialogs */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Connection"
        description="Are you sure you want to delete this connection? This action cannot be undone."
        onConfirm={handleDeleteConnection}
        confirmText={isDeletingGrant ? 'Deleting...' : 'Delete'}
        confirmVariant="destructive"
        confirmButtonClassName="bg-destructive text-destructive-foreground hover:bg-destructive/90"
      />

      <ConfirmationDialog
        open={isDeleteWebhookDialogOpen}
        onOpenChange={setIsDeleteWebhookDialogOpen}
        title="Delete Webhook"
        description="Are you sure you want to delete this webhook? This action cannot be undone."
        onConfirm={handleDeleteWebhook}
        confirmText={isDeletingWebhook ? 'Deleting...' : 'Delete'}
        confirmVariant="destructive"
        confirmButtonClassName="bg-destructive text-destructive-foreground hover:bg-destructive/90"
      />
    </Container>
  );
}

export default SettingsPage;
